import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import VideoGrid from "@/components/VideoGrid";
import TransferPanel from "@/components/TransferPanel";
import SearchBar from "@/components/SearchBar";
import Onboarding from "@/components/Onboarding";
import ConnectServices from "@/components/ConnectServices";
import GoldConfetti from "@/components/GoldConfetti";
import DestinationInfo from "@/components/DestinationInfo";
import FolderPicker from "@/components/FolderPicker";
import AdminPanel from "@/components/AdminPanel";
import { CloudUpload, RefreshCw, CheckSquare, Square, Loader2, Download, Settings, CheckCircle, Clock, HardDrive, AlertCircle, MonitorSmartphone } from "lucide-react";
import ConfirmModal from "@/components/ConfirmModal";
import { startTransfers, cancelTransfer, cancelAllTransfers } from "@/lib/transferEngine";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// axios.defaults.withCredentials set globally in index.js

const QUALITY_LABELS = {
  source: "Original",
  "4k": "4K (2160p)",
  high: "High (1080p+)",
  medium: "Medium (720p)",
  low: "Low (360p)",
};

const PER_PAGE = 100;

const LOADING_QUIPS = [
  "pretending to be a hard drive with feelings",
  "this is still faster than rendering in 8K",
  "asking vimeo nicely for your footage",
  "your timeline called — it misses you",
  "counting frames like sheep",
  "somewhere, a DJ is asking 'is this thing on?'",
  "loading faster than a groomsman finding the rings",
  "buffering like it's 2008 all over again",
  "organizing pixels by emotional weight",
  "checking if the groom cried (spoiler: he did)",
  "currently faster than exporting from premiere",
  "scanning for jump cuts and happy tears",
  "your storage is about to feel very loved",
  "downloading memories, no pressure",
  "moving files like a second shooter moves through a crowd",
  "hold tight — this is the boring part of the highlight reel",
  "transferring at the speed of 'I do'",
  "vimeo is handing over the footage. gently.",
  "this would go faster if we skipped the toasts",
  "your hard drives thank you for this",
  "somewhere a bride just asked 'did you get that?'",
  "packaging your life's work. no big deal.",
  "currently outperforming hotel wifi",
  "like waiting for the first look, but with progress bars",
  "backing up the backup of the backup",
  "your footage is in good hands. robot hands, but still.",
  "loading slower than uncle bob with his iPad",
  "preparing files like a florist prepares arrangements",
  "may your proxies always be low-res in peace",
  "don't worry, we won't add a watermark",
  "treat yourself — go color grade something while you wait",
  "officially more reliable than a shot list",
  "moving terabytes like a videographer moves through a dance floor",
  "compiling a highlight reel of your highlight reels",
  "if this takes too long, blame the aperture",
  "your vault is about to be stacked",
  "this is the digital equivalent of wrapping cables",
  "gathering footage like confetti at the send-off",
  "one does not simply download 3,700 videos",
  "DaVinci Resolve could never",
];

export default function Dashboard() {
  // Auth state
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authStep, setAuthStep] = useState("loading"); // "loading" | "onboarding" | "connect" | "dashboard"
  const [signupLoading, setSignupLoading] = useState(false);

  // Dashboard state
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUris, setSelectedUris] = useState(new Set());
  const [transferring, setTransferring] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [showTransfers, setShowTransfers] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [health, setHealth] = useState(null);
  const [quality, setQuality] = useState("source");
  const [destination, setDestination] = useState("dropbox");
  const [durationFilter, setDurationFilter] = useState("all");
  const [vimeoPlanWarning, setVimeoPlanWarning] = useState(false);
  const [transferAllLoading, setTransferAllLoading] = useState(false);
  const [transferAllMessage, setTransferAllMessage] = useState("");
  const [transferAllPhase, setTransferAllPhase] = useState(""); // "fetching" | "done" | ""
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", description: "", hint: "", confirmLabel: "", variant: "default", onConfirm: () => {} });
  const [loadingQuip, setLoadingQuip] = useState(() => LOADING_QUIPS[Math.floor(Math.random() * LOADING_QUIPS.length)]);
  const quipRef = useRef(null);
  const [completedUris, setCompletedUris] = useState(new Set());
  const [vaultTotalCount, setVaultTotalCount] = useState(0);
  const [vaultMap, setVaultMap] = useState({}); // uri → ["dropbox", "gdrive"]
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPrefs, setFolderPrefs] = useState({ dropbox: { path: "", name: "Vimeo Backups" }, gdrive: { path: "", name: "Vimeo Backups" } });
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const prevCompletedCount = useRef(0);
  const pollRef = useRef(null);

  const fetchVideos = useCallback(async (pg = 1, q = "", durFilter = "all") => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pg, per_page: PER_PAGE };
      if (q.trim()) params.q = q.trim();
      const durMap = { under1: [0, 59], "1to5": [60, 300], "5to20": [300, 1200], over20: [1200, null] };
      if (durFilter !== "all" && durMap[durFilter]) {
        const [min, max] = durMap[durFilter];
        if (min != null) params.min_duration = min;
        if (max != null) params.max_duration = max;
      }
      const res = await axios.get(`${API}/videos`, { params });
      if (res.data.error) {
        setError(res.data.error);
        setVideos([]);
      } else {
        const vids = res.data.videos || [];
        setVideos(vids);
        setTotalVideos(res.data.total || 0);
        setHasNext(res.data.has_next);
        setHasPrev(res.data.has_prev);
        // Check if any videos have downloads — warn if none do
        if (vids.length > 0 && !vids.some(v => v.has_download)) {
          setVimeoPlanWarning(true);
        } else if (vids.some(v => v.has_download)) {
          setVimeoPlanWarning(false);
        }
      }
    } catch (err) {
      setError(err.message);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompletedUris = useCallback(async (withDropbox = false, dest = null) => {
    try {
      const params = {};
      if (withDropbox) params.include_dropbox = true;
      if (dest) params.destination = dest;
      const res = await axios.get(`${API}/completed-uris`, { params });
      setCompletedUris(new Set(res.data.uris || []));
      setVaultTotalCount(res.data.count || res.data.uris?.length || 0);
      if (res.data.vault_map) setVaultMap(res.data.vault_map);
    } catch { /* silent */ }
  }, []);

  const fetchFolderPrefs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/folder-preference`);
      setFolderPrefs(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/health`);
      setHealth(res.data);
    } catch { setHealth(null); }
  }, []);

  const fetchTransfers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transfers`);
      const newTransfers = res.data.transfers || [];
      setTransfers(newTransfers);
      // Detect newly completed transfers for confetti
      const newCompleted = newTransfers.filter(t => t.status === "completed").length;
      if (newCompleted > prevCompletedCount.current && prevCompletedCount.current > 0) {
        setConfettiTrigger(prev => prev + 1);
      }
      prevCompletedCount.current = newCompleted;
    } catch { /* silent */ }
  }, []);

  // Auth check on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/me`);
        if (res.data.authenticated && res.data.user) {
          setCurrentUser(res.data.user);
          const u = res.data.user;
          const hasVimeo = !!u.vimeo_token;
          const hasDestination = !!u.dropbox_token || !!u.gdrive_token;
          if (u.mode === "static" || (hasVimeo && hasDestination)) {
            setAuthStep("dashboard");
          } else {
            setAuthStep("connect");
          }
        } else {
          setAuthStep("onboarding");
        }
      } catch {
        setAuthStep("onboarding");
      }
      setAuthChecked(true);
    };
    checkAuth();
    // Check URL params for OAuth callbacks
    const params = new URLSearchParams(window.location.search);
    if (params.get("vimeo") === "connected" || params.get("dropbox") === "connected") {
      window.history.replaceState({}, "", "/");
    }
  }, []);

  // Load dashboard data when entering dashboard step
  useEffect(() => {
    if (authStep === "dashboard") {
      fetchVideos(1, "", "all");
      fetchHealth();
      fetchTransfers();
      fetchCompletedUris(true, destination);
      fetchFolderPrefs();  // Include Dropbox check, filter by destination
    }
  }, [authStep, fetchVideos, fetchHealth, fetchTransfers, fetchCompletedUris]);

  // Resume pending transfers in the browser on page load
  useEffect(() => {
    if (authStep !== "dashboard") return;
    const resumeTimer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/transfers`);
        const pending = (res.data.transfers || [])
          .filter(t => t.status === "pending")
          .map(t => t.id);
        if (pending.length > 0) {
          startTransfers(pending, () => fetchTransfers());
        }
      } catch { /* silent */ }
    }, 3000); // Wait 3s for UI to settle
    return () => clearTimeout(resumeTimer);
  }, [authStep, fetchTransfers]);

  const handleVerified = (data) => {
    const u = data.user;
    setCurrentUser(u);
    const hasVimeo = !!u.vimeo_token;
    const hasDestination = !!u.dropbox_token || !!u.gdrive_token;
    if (u.mode === "static" || (hasVimeo && hasDestination)) {
      setAuthStep("dashboard");
    } else {
      setAuthStep("connect");
    }
    if (data.returning) {
      if (hasVimeo && hasDestination) {
        toast.success("Welcome back — picking up where you left off");
      } else {
        toast.success("Welcome back to the vault");
      }
    } else {
      toast.success("You're in. Let's connect your accounts.");
    }
  };

  const handleConnectVimeo = async () => {
    try {
      const res = await axios.get(`${API}/auth/vimeo`);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(res.data.error || "Vimeo OAuth not configured yet");
      }
    } catch { toast.error("Failed to start Vimeo connection"); }
  };

  const handleConnectDropbox = async () => {
    try {
      const res = await axios.get(`${API}/auth/dropbox`);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(res.data.error || "Dropbox OAuth not configured yet");
      }
    } catch { toast.error("Failed to start Dropbox connection"); }
  };

  // Always poll transfers — fast when active, slow baseline to catch new jobs
  useEffect(() => {
    if (authStep !== "dashboard") return;
    const hasActive = transfers.some(t => ["pending", "downloading", "uploading"].includes(t.status));
    const interval = hasActive ? 2000 : 5000; // 2s when active, 5s baseline
    pollRef.current = setInterval(() => {
      fetchTransfers();
      if (hasActive) fetchCompletedUris();
    }, interval);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [transfers, fetchTransfers, fetchCompletedUris, authStep]);

  // Rotate loading quips every 4 seconds
  useEffect(() => {
    if (transferAllLoading && transferAllPhase === "fetching") {
      quipRef.current = setInterval(() => {
        setLoadingQuip(LOADING_QUIPS[Math.floor(Math.random() * LOADING_QUIPS.length)]);
      }, 4000);
    } else {
      if (quipRef.current) clearInterval(quipRef.current);
    }
    return () => { if (quipRef.current) clearInterval(quipRef.current); };
  }, [transferAllLoading, transferAllPhase]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    setPage(1);
    setSelectedUris(new Set());
    fetchVideos(1, q, durationFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedUris(new Set());
    fetchVideos(newPage, searchQuery, durationFilter);
  };

  const handleDurationFilter = (val) => {
    setDurationFilter(val);
    setPage(1);
    setSelectedUris(new Set());
    setTotalVideos(0);
    setVideos([]);
    fetchVideos(1, searchQuery, val);
  };

  const toggleSelect = (uri) => {
    setSelectedUris(prev => {
      const next = new Set(prev);
      if (next.has(uri)) next.delete(uri);
      else next.add(uri);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedUris.size === videos.length) {
      setSelectedUris(new Set());
    } else {
      setSelectedUris(new Set(videos.map(v => v.uri)));
    }
  };

  const startTransfer = async () => {
    const uris = Array.from(selectedUris);
    if (uris.length === 0) { toast.error("Select at least one video"); return; }
    setTransferring(true);
    try {
      const res = await axios.post(`${API}/transfer`, {
        video_uris: uris,
        video_names: Object.fromEntries(videos.filter(v => uris.includes(v.uri)).map(v => [v.uri, v.name])),
        quality,
        destination,
      });
      if (res.data.error) { toast.error(res.data.error); }
      else {
        const msg = res.data.message || `${res.data.total} queued`;
        if (res.data.skipped_duplicate > 0 && res.data.total > 0) {
          toast.success(msg);
        } else if (res.data.total === 0 && res.data.skipped_duplicate > 0) {
          toast.info(msg);
        } else {
          toast.success(msg);
        }
        // Start client-side transfers
        const jobIds = (res.data.jobs || []).map(j => j.id);
        if (jobIds.length > 0) {
          startTransfers(jobIds, () => fetchTransfers());
          setShowTransfers(true);
        }
        setSelectedUris(new Set());
        fetchTransfers();
      }
    } catch (err) { toast.error("Transfer failed: " + err.message); }
    finally { setTransferring(false); }
  };

  const transferAll = async () => {
    const estNew = Math.max(0, totalVideos - vaultCount);
    const label = searchQuery.trim() ? `"${searchQuery}"` : "all";

    let title, description, hint;
    if (vaultCount > 0 && estNew > 0) {
      title = `Transfer ${estNew} New Videos`;
      description = `${estNew} new ${label} videos will be transferred to Dropbox at ${QUALITY_LABELS[quality]} quality. ${vaultCount} already in your vault will be skipped automatically.`;
      hint = "// only new footage gets transferred — your vault stays clean";
    } else if (vaultCount > 0 && estNew === 0) {
      title = "All Videos in Vault";
      description = `All ${vaultCount} ${label} videos appear to be in your vault already. You can run this anyway to double-check against Dropbox.`;
      hint = "// this will verify your vault against dropbox — nothing will be re-downloaded";
    } else {
      title = `Transfer ${totalVideos} Videos`;
      description = `${totalVideos} ${label} videos will be transferred to Dropbox at ${QUALITY_LABELS[quality]} quality.`;
      hint = "// this may take a moment to gather video info from vimeo";
    }

    setConfirmModal({
      open: true,
      title,
      description,
      hint,
      confirmLabel: estNew > 0 ? `Transfer ${estNew} New` : "Verify Vault",
      variant: "default",
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        _executeTransferAll();
      },
    });
  };

  const _executeTransferAll = async () => {
    setTransferAllLoading(true);
    setTransferAllMessage(`Gathering ${searchQuery.trim() ? `"${searchQuery}" ` : ""}videos from Vimeo...`);
    setTransferAllPhase("fetching");
    try {
      const params = { quality, destination };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      const durMap = { under1: [0, 59], "1to5": [60, 300], "5to20": [300, 1200], over20: [1200, null] };
      if (durationFilter !== "all" && durMap[durationFilter]) {
        const [min, max] = durMap[durationFilter];
        if (min != null) params.min_duration = min;
        if (max != null) params.max_duration = max;
      }

      // Step 1: Start the batch (returns immediately)
      const res = await axios.post(`${API}/transfer-all`, null, { params, timeout: 30000 });
      if (res.data.error) {
        setTransferAllPhase("error");
        setTransferAllMessage(res.data.error);
        await new Promise(r => setTimeout(r, 3000));
        return;
      }

      const batchId = res.data.batch_id;
      if (!batchId) {
        setTransferAllPhase("error");
        setTransferAllMessage("Failed to start batch");
        await new Promise(r => setTimeout(r, 3000));
        return;
      }

      // Step 2: Poll batch status until done
      let attempts = 0;
      const maxAttempts = 300; // 300 * 2s = 10 minutes max
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/batch/${batchId}`);
          const batch = statusRes.data;

          if (batch.status === "done") {
            setTransferAllPhase("done");
            setTransferAllMessage(batch.message);
            await new Promise(r => setTimeout(r, 2000));
            if (batch.queued > 0) {
              toast.success(batch.message);
              setShowTransfers(true);
            } else {
              toast.info(batch.message);
            }
            // Fetch pending transfers and start them in the browser
            try {
              const tRes = await axios.get(`${API}/transfers`);
              const pendingIds = (tRes.data.transfers || []).filter(t => t.status === "pending").map(t => t.id);
              if (pendingIds.length > 0) startTransfers(pendingIds, () => fetchTransfers());
            } catch {}
            fetchTransfers();
            fetchCompletedUris();
            setTransferAllLoading(false);
            setTransferAllMessage("");
            setTransferAllPhase("");
            return;
          } else if (batch.status === "error") {
            setTransferAllPhase("error");
            setTransferAllMessage(batch.message || "Something went wrong. Try smaller batches.");
            // Don't clear — error screen has a Dismiss button
            return;
          }
          // Still scanning — update the message
          setTransferAllMessage(batch.message || "Scanning your Vimeo library...");
        } catch {
          // Poll failed — keep trying
        }
      }

      // Timed out — backend is still working. Navigate to transfers and start what's pending.
      setShowTransfers(true);
      try {
        const tRes = await axios.get(`${API}/transfers`);
        const pendingIds = (tRes.data.transfers || []).filter(t => t.status === "pending").map(t => t.id);
        if (pendingIds.length > 0) startTransfers(pendingIds, () => fetchTransfers());
      } catch {}
      fetchTransfers();
      setTransferAllLoading(false);
      setTransferAllMessage("");
      setTransferAllPhase("");

    } catch (err) {
      setTransferAllPhase("error");
      setTransferAllMessage("Something went wrong starting the transfer. Try again, or transfer in smaller batches using the search filter.");
    }
    // Note: don't auto-hide on error. The error screen has a "Dismiss" button.
    // For success/done, the overlay was already hidden inside the polling loop via return + then the code below runs
  };

  const dismissTransferAllOverlay = () => {
    setTransferAllLoading(false);
    setTransferAllMessage("");
    setTransferAllPhase("");
  };

  const clearHistory = async () => {
    setConfirmModal({
      open: true,
      title: "Clear Transfer History",
      description: "This removes all transfer records from GemVault. Your videos in Dropbox are completely safe — nothing gets deleted there. GemVault will still detect existing Dropbox files on your next transfer.",
      hint: "// your dropbox files are untouched — this only clears the log",
      confirmLabel: "Clear History",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API}/transfers`);
          setTransfers([]);
          setCompletedUris(new Set());
          toast.success("History cleared — your Dropbox files are untouched");
          fetchCompletedUris();
        } catch { toast.error("Failed to clear history"); }
      },
    });
  };

  const cancelOne = async (jobId) => {
    cancelTransfer(jobId);
    try { await axios.post(`${API}/transfer/${jobId}/cancel`); toast.success("Transfer cancelled"); fetchTransfers(); }
    catch { toast.error("Failed to cancel"); }
  };

  const cancelAll = async () => {
    cancelAllTransfers();
    try { const res = await axios.post(`${API}/transfers/cancel-all`); toast.success(res.data.message); fetchTransfers(); }
    catch { toast.error("Failed to cancel"); }
  };

  const retryFailed = async () => {
    try {
      const res = await axios.post(`${API}/transfers/retry-failed`);
      if (res.data.error) {
        toast.error(res.data.error);
        return;
      }
      if (res.data.retried > 0) {
        toast.success(res.data.message);
        // Start all new pending transfers in the browser
        const tRes = await axios.get(`${API}/transfers`);
        const pending = (tRes.data.transfers || []).filter(t => t.status === "pending").map(t => t.id);
        if (pending.length > 0) startTransfers(pending, () => fetchTransfers());
      } else {
        toast.info(res.data.message);
      }
      fetchTransfers();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to retry");
    }
  };

  const retryOne = async (jobId) => {
    try {
      const res = await axios.post(`${API}/transfer/${jobId}/retry`);
      if (res.data.error) {
        toast.error(res.data.error);
        return;
      }
      toast.success(res.data.message || "Transfer re-queued");
      // Start the specific new job
      if (res.data.new_id) {
        startTransfers([res.data.new_id], () => fetchTransfers());
      }
      fetchTransfers();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to retry";
      toast.error(msg);
    }
  };

  const clearFailed = async () => {
    try {
      const res = await axios.delete(`${API}/transfers/failed`);
      toast.success(res.data.message);
      fetchTransfers();
    } catch { toast.error("Failed to clear"); }
  };

  const allSelected = videos.length > 0 && selectedUris.size === videos.length;
  const isAdmin = currentUser?.email && ["rgelderm@gmail.com", "ryan@uncutgems.com", "ryan.geldermann@gmail.com", "hello@uncutgems.com"].includes(currentUser.email.toLowerCase());

  // Compute vault awareness for current view
  const pageInVault = videos.filter(v => completedUris.has(v.uri)).length;
  const pageNew = videos.length - pageInVault;
  const vaultCount = Math.max(completedUris.size, vaultTotalCount);

  // Auth loading
  if (!authChecked || authStep === "loading") {
    return (
      <div className="min-h-screen bg-[#0f1114] flex items-center justify-center relative z-10">
        <Loader2 className="h-8 w-8 animate-spin text-[#c6a96f]" />
      </div>
    );
  }

  // Onboarding: email signup
  if (authStep === "onboarding") {
    return <Onboarding onVerified={handleVerified} loading={signupLoading} setLoading={setSignupLoading} />;
  }

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/logout`);
      setCurrentUser(null);
      setAuthStep("onboarding");
      setTransfers([]);
      setVideos([]);
      setCompletedUris(new Set());
      toast.success("Logged out");
    } catch {
      toast.error("Failed to log out");
    }
  };

  // Connect services
  if (authStep === "connect") {
    return (
      <ConnectServices
        user={currentUser}
        onUserUpdated={(u) => setCurrentUser(u)}
        onContinue={() => setAuthStep("dashboard")}
      />
    );
  }

  // Dashboard
  return (
    <div data-testid="dashboard" className="min-h-screen bg-[#0f1114] text-[#d4d4d8] flex flex-col md:flex-row relative z-10">
      <Sidebar
        health={health}
        transferCount={transfers.filter(t => ["pending", "downloading", "uploading"].includes(t.status)).length}
        onShowTransfers={() => { setShowTransfers(true); setShowAdmin(false); }}
        showTransfers={showTransfers && !showAdmin}
        onShowVideos={() => { setShowTransfers(false); setShowAdmin(false); }}
        onHealthRefresh={fetchHealth}
        userEmail={currentUser?.email}
        onLogout={handleLogout}
        isAdmin={isAdmin}
        showAdmin={showAdmin}
        onShowAdmin={() => { setShowAdmin(true); setShowTransfers(false); }}
      />

      <main className="flex-1 p-5 md:p-10 overflow-y-auto bg-[#131518]">
        {showAdmin && isAdmin ? (
          <AdminPanel onBack={() => setShowAdmin(false)} />
        ) : !showTransfers ? (
          <>
            {/* Header */}
            <div className="mb-10">
              <div className="section-label mb-3">// your vimeo library</div>
              <h1 data-testid="page-title" className="text-3xl md:text-4xl font-bold tracking-tight text-[#f4f4f5]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {searchQuery.trim() ? `Results for "${searchQuery}"` : "Your Videos"}
              </h1>
              <p className="text-sm text-[#71717a] mt-2">
                {totalVideos > 0
                  ? <span className="font-mono text-[12px]">
                      {totalVideos} video{totalVideos !== 1 ? 's' : ''} {durationFilter !== "all" ? 'match your filter' : searchQuery.trim() ? 'found' : 'in your library'}
                      {vaultCount > 0 && <span className="text-[#5fa87a]"> &middot; {vaultCount} already in vault</span>}
                    </span>
                  : 'Browse and transfer your Vimeo videos'}
              </p>
            </div>

            {/* Vimeo plan warning */}
            {vimeoPlanWarning && (
              <div data-testid="vimeo-plan-warning" className="ug-card p-5 mb-6 !border-[#c47272]/30">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-5 w-5 text-[#c47272] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#e4e4e7] mb-1">Your Vimeo plan doesn't support transfers</p>
                    <p className="text-xs text-[#a1a1aa] leading-relaxed mb-2">
                      GemVault requires <span className="text-[#c6a96f] font-medium">Vimeo Advanced</span> or higher to download videos via the API. Your current plan (Starter or Standard) doesn't include this feature.
                    </p>
                    <a href="https://vimeo.com/upgrade" target="_blank" rel="noopener noreferrer" className="text-xs text-[#c6a96f] hover:text-[#d4b97e] transition-colors font-medium">
                      Upgrade your Vimeo plan →
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="space-y-4 mb-8">
              <SearchBar onSearch={handleSearch} />

              {/* Row 1: Filters */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-mono text-[#52525b] tracking-wider uppercase mr-1">Filter</span>
                <Select value={durationFilter} onValueChange={handleDurationFilter}>
                  <SelectTrigger data-testid="duration-select" className="w-[145px] h-9 text-xs bg-[#21252b] border-[#2a2e36] text-[#a1a1aa] rounded-xl focus:ring-[#a08a58]">
                    <Clock className="h-3.5 w-3.5 mr-1.5 text-[#52525b]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1d21] border-[#2a2e36]">
                    <SelectItem value="all">All Lengths</SelectItem>
                    <SelectItem value="under1">Under 1 min</SelectItem>
                    <SelectItem value="1to5">1 - 5 min</SelectItem>
                    <SelectItem value="5to20">5 - 20 min</SelectItem>
                    <SelectItem value="over20">20+ min</SelectItem>
                  </SelectContent>
                </Select>

                {/* Filtered count */}
                {durationFilter !== "all" && totalVideos > 0 && (
                  <span className="text-[11px] text-[#c6a96f] font-mono">
                    {totalVideos} matching
                  </span>
                )}

                <div className="ml-auto flex items-center gap-3">
                  <button data-testid="select-all-btn" onClick={selectAll} disabled={videos.length === 0} className="btn-ghost px-3 py-2 rounded-xl text-xs flex items-center gap-2 disabled:opacity-30">
                    {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                    {allSelected ? "Deselect" : "Select All"}
                  </button>
                  <button data-testid="refresh-btn" onClick={() => fetchVideos(page, searchQuery, durationFilter)} className="btn-ghost px-3 py-2 rounded-xl text-xs flex items-center gap-2">
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                  </button>
                </div>
              </div>

              {/* Row 2: Transfer settings + actions */}
              <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[#222630]">
                <span className="text-[10px] font-mono text-[#52525b] tracking-wider uppercase mr-1">Transfer</span>

                <Select value={quality} onValueChange={setQuality}>
                  <SelectTrigger data-testid="quality-select" className="w-[155px] h-9 text-xs bg-[#21252b] border-[#2a2e36] text-[#a1a1aa] rounded-xl focus:ring-[#a08a58]">
                    <Settings className="h-3.5 w-3.5 mr-1.5 text-[#52525b]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1d21] border-[#2a2e36]">
                    <SelectItem value="source">Original Quality</SelectItem>
                    <SelectItem value="4k">4K (2160p)</SelectItem>
                    <SelectItem value="high">High (1080p+)</SelectItem>
                    <SelectItem value="medium">Medium (720p)</SelectItem>
                    <SelectItem value="low">Low (360p)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={destination} onValueChange={(val) => { setDestination(val); fetchCompletedUris(val === "dropbox", val); }}>
                  <SelectTrigger data-testid="destination-select" className="w-[150px] h-9 text-xs bg-[#21252b] border-[#2a2e36] text-[#a1a1aa] rounded-xl focus:ring-[#a08a58]">
                    <HardDrive className="h-3.5 w-3.5 mr-1.5 text-[#52525b]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1d21] border-[#2a2e36]">
                    <SelectItem value="dropbox">To Dropbox</SelectItem>
                    <SelectItem value="gdrive">To Google Drive</SelectItem>
                  </SelectContent>
                </Select>

                <div className="ml-auto flex items-center gap-3">
                  <button
                    data-testid="transfer-btn"
                    onClick={startTransfer}
                    disabled={selectedUris.size === 0 || transferring}
                    className="btn-gold px-4 py-2 rounded-xl text-xs flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {transferring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}
                    Transfer{selectedUris.size > 0 ? ` (${selectedUris.size})` : ""}
                  </button>

                  <button
                    data-testid="transfer-all-btn"
                    onClick={transferAll}
                    disabled={transferAllLoading || totalVideos === 0}
                    className="btn-ghost px-4 py-2 rounded-xl text-xs flex items-center gap-2 !border-[#c6a96f]/30 !text-[#c6a96f] hover:!bg-[rgba(198,169,111,0.08)] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {transferAllLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    {vaultCount > 0 && totalVideos > vaultCount
                      ? `Transfer ${totalVideos - vaultCount} New`
                      : vaultCount > 0 && totalVideos <= vaultCount
                      ? "All in Vault"
                      : `Transfer All (${totalVideos})`
                    }
                  </button>
                </div>
              </div>
            </div>

            {/* Transfer All loading banner */}
            {transferAllLoading && (
              <div data-testid="transfer-all-loading" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(15, 17, 20, 0.92)' }}>
                <div className="max-w-md w-full mx-4 text-center">
                  {transferAllPhase === "fetching" && (
                    <>
                      {/* Animated gold ring */}
                      <div className="relative mx-auto mb-8 w-20 h-20">
                        <div className="absolute inset-0 rounded-full border-2 border-[#2a2e36]" />
                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#c6a96f] animate-spin" />
                        <div className="absolute inset-3 rounded-full border border-transparent border-b-[#c6a96f]/40 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
                      </div>
                      <p className="text-lg font-semibold text-[#f4f4f5] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                        Gathering Your Footage
                      </p>
                      <p className="text-sm text-[#a1a1aa] mb-6">
                        {transferAllMessage}
                      </p>
                      <p className="text-[11px] text-[#c6a96f] font-mono tracking-wider transition-opacity duration-500" key={loadingQuip}>
                        // {loadingQuip}
                      </p>
                    </>
                  )}
                  {transferAllPhase === "done" && (
                    <>
                      <div className="mx-auto mb-8 w-20 h-20 rounded-full border-2 border-[#5fa87a] flex items-center justify-center">
                        <CheckCircle className="h-10 w-10 text-[#5fa87a]" />
                      </div>
                      <p className="text-lg font-semibold text-[#f4f4f5] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                        Queue Loaded
                      </p>
                      <p className="text-sm text-[#a1a1aa]">
                        {transferAllMessage}
                      </p>
                      <p className="text-[10px] text-[#52525b] font-mono tracking-wider mt-4">
                        // taking you to the transfer queue...
                      </p>
                    </>
                  )}
                  {transferAllPhase === "error" && (
                    <>
                      <div className="mx-auto mb-8 w-20 h-20 rounded-full border-2 border-[#c47272] flex items-center justify-center">
                        <AlertCircle className="h-10 w-10 text-[#c47272]" />
                      </div>
                      <p className="text-lg font-semibold text-[#f4f4f5] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                        Transfer Hit a Snag
                      </p>
                      <p className="text-sm text-[#a1a1aa] leading-relaxed mb-6">
                        {transferAllMessage}
                      </p>
                      <button
                        onClick={dismissTransferAllOverlay}
                        className="btn-ghost px-6 py-2.5 rounded-xl text-xs"
                      >
                        Dismiss
                      </button>
                      <p className="text-[10px] text-[#52525b] font-mono tracking-wider mt-4">
                        // try transferring in smaller batches using the search filter
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Destination info — clickable to change folder */}
            <DestinationInfo
              destination={destination}
              folderName={folderPrefs[destination]?.name}
              onClick={() => setFolderPickerOpen(true)}
            />

            {/* Folder picker modal */}
            <FolderPicker
              open={folderPickerOpen}
              onClose={() => setFolderPickerOpen(false)}
              destination={destination}
              onSelect={(path, name) => {
                setFolderPrefs(prev => ({ ...prev, [destination]: { path, name: name || "Vimeo Backups" } }));
              }}
            />

            {/* Video Grid */}
            <VideoGrid
              videos={videos}
              loading={loading}
              error={error}
              selectedUris={selectedUris}
              completedUris={completedUris}
              vaultMap={vaultMap}
              onToggleSelect={toggleSelect}
              vimeoConnected={health?.vimeo?.connected}
              dropboxConnected={health?.dropbox?.connected}
              gdriveConnected={health?.gdrive?.connected}
              onRefresh={() => fetchVideos(page, searchQuery, durationFilter)}
            />

            {/* Pagination */}
            {(hasNext || hasPrev) && (
              <div data-testid="pagination" className="flex items-center justify-center gap-4 mt-10">
                <button data-testid="prev-page-btn" disabled={!hasPrev} onClick={() => handlePageChange(page - 1)} className="btn-ghost px-4 py-2 rounded-xl text-xs disabled:opacity-30">
                  Previous
                </button>
                <span className="text-[11px] text-[#52525b] font-mono">
                  pg {page}{totalVideos > 0 ? ` of ${Math.ceil(totalVideos / PER_PAGE)}` : ""}
                </span>
                <button data-testid="next-page-btn" disabled={!hasNext} onClick={() => handlePageChange(page + 1)} className="btn-ghost px-4 py-2 rounded-xl text-xs disabled:opacity-30">
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Transfers run on our servers — safe to close tab */}
            {transfers.some(t => ["pending", "downloading", "uploading"].includes(t.status)) && (
              <div data-testid="transfer-background-notice" className="ug-card p-4 mb-6 flex items-center gap-4 !border-[#5ba89d]/30">
                <MonitorSmartphone className="h-5 w-5 text-[#5ba89d] flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#e4e4e7]">Transfers run in the background</p>
                  <p className="text-[10px] text-[#52525b] font-mono mt-0.5">// you can close this tab or turn off your computer — we’ll keep moving your footage to Dropbox</p>
                </div>
              </div>
            )}
            <TransferPanel
            transfers={transfers}
            onClearHistory={clearHistory}
            onRefresh={fetchTransfers}
            onCancelOne={cancelOne}
            onCancelAll={cancelAll}
            onRetryFailed={retryFailed}
            onRetryOne={retryOne}
            onClearFailed={clearFailed}
          />
          </>
        )}
      </main>

      {/* Confirm Modal — replaces all window.confirm() */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        description={confirmModal.description}
        hint={confirmModal.hint}
        confirmLabel={confirmModal.confirmLabel}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      {/* Gold confetti on transfer completion */}
      <GoldConfetti trigger={confettiTrigger} />
    </div>
  );
}
